// /app/frontend/src/pages/risk/dpia/steps/DataSharingStep.jsx
import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Card, CardContent } from '../../../../components/ui/card';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Plus, Trash2, Share2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

export default function DataSharingStep({ formData, updateField }) {
  const addThirdParty = () => {
    const newParty = {
      id: uuidv4(),
      name: '',
      role: 'Processor',
      contract_in_place: false,
      dpa_in_place: false,
      security_commitments: '',
    };
    updateField('third_parties', [...formData.third_parties, newParty]);
  };

  const updateThirdParty = (partyId, field, value) => {
    const updated = formData.third_parties.map(party => 
      party.id === partyId ? { ...party, [field]: value } : party
    );
    updateField('third_parties', updated);
  };

  const removeThirdParty = (partyId) => {
    updateField('third_parties', formData.third_parties.filter(p => p.id !== partyId));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-foreground">Data Sharing</h3>
        <p className="text-sm text-muted-foreground">Document internal data sharing and third-party recipients</p>
      </div>

      {/* Internal Data Sharing Section */}
      <Card className="border border-border">
        <CardContent className="pt-6 space-y-4">
          <div>
            <h4 className="font-medium text-base">Internal Data Sharing</h4>
            <p className="text-sm text-muted-foreground">Sharing within your organisation</p>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox 
              id="internal_sharing" 
              checked={formData.internal_sharing}
              onCheckedChange={(checked) => updateField('internal_sharing', checked)}
              className="data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
              data-testid="internal-sharing-checkbox"
            />
            <Label htmlFor="internal_sharing" className="text-sm cursor-pointer">
              Personal data will be shared internally
            </Label>
          </div>

          {formData.internal_sharing && (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Internal Recipients <span className="text-red-500">*</span></Label>
                <Textarea 
                  value={formData.internal_sharing_details}
                  onChange={(e) => updateField('internal_sharing_details', e.target.value)}
                  placeholder="List departments/teams that will receive the data..."
                  rows={3}
                  className="resize-none"
                  data-testid="internal-recipients-textarea"
                />
              </div>
              <div className="space-y-2">
                <Label>Reasons for Sharing</Label>
                <Textarea 
                  value={formData.internal_sharing_reasons}
                  onChange={(e) => updateField('internal_sharing_reasons', e.target.value)}
                  placeholder="Explain why data needs to be shared internally..."
                  rows={3}
                  className="resize-none"
                  data-testid="sharing-reasons-textarea"
                />
              </div>
              <div className="space-y-2">
                <Label>Sharing Method</Label>
                <Input 
                  value={formData.internal_sharing_method}
                  onChange={(e) => updateField('internal_sharing_method', e.target.value)}
                  placeholder="e.g., Secure file share, Email encryption, Internal system..."
                  data-testid="sharing-method-input"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Third Party Recipients Section */}
      <Card className="border border-border">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-base">Third Party Recipients</h4>
              <p className="text-sm text-muted-foreground">External organisations receiving data</p>
            </div>
            <Button 
              variant="default" 
              size="sm" 
              onClick={addThirdParty}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="add-third-party-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add Third Party
            </Button>
          </div>

          {formData.third_parties.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Share2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No third parties added yet.</p>
              <p className="text-xs">Click "Add Third Party" to document external data sharing.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {formData.third_parties.map((party, idx) => (
                <Card key={party.id} className="border border-border bg-muted/20">
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h5 className="font-medium text-sm">Third Party #{idx + 1}</h5>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeThirdParty(party.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`remove-third-party-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>Organisation Name <span className="text-red-500">*</span></Label>
                      <Input 
                        placeholder="Name of Data Processor or Company Data is shared with"
                        value={party.name}
                        onChange={(e) => updateThirdParty(party.id, 'name', e.target.value)}
                        data-testid={`third-party-name-${idx}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <Select 
                          value={party.role || 'Processor'}
                          onValueChange={(v) => updateThirdParty(party.id, 'role', v)}
                        >
                          <SelectTrigger data-testid={`third-party-role-${idx}`}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Processor">Processor</SelectItem>
                            <SelectItem value="Joint Controller">Joint Controller</SelectItem>
                            <SelectItem value="Controller">Controller</SelectItem>
                            <SelectItem value="Sub-processor">Sub-processor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex flex-col justify-end space-y-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id={`contract-${party.id}`}
                            checked={party.contract_in_place}
                            onCheckedChange={(checked) => updateThirdParty(party.id, 'contract_in_place', checked)}
                            className="data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
                            data-testid={`third-party-contract-${idx}`}
                          />
                          <Label htmlFor={`contract-${party.id}`} className="text-sm cursor-pointer">
                            Contract in place
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id={`dpa-${party.id}`}
                            checked={party.dpa_in_place}
                            onCheckedChange={(checked) => updateThirdParty(party.id, 'dpa_in_place', checked)}
                            className="data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
                            data-testid={`third-party-dpa-${idx}`}
                          />
                          <Label htmlFor={`dpa-${party.id}`} className="text-sm cursor-pointer">
                            DPA in place
                          </Label>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Security Commitments</Label>
                      <Textarea 
                        placeholder="Technical and Organisational measures to protect the Data"
                        value={party.security_commitments}
                        onChange={(e) => updateThirdParty(party.id, 'security_commitments', e.target.value)}
                        rows={3}
                        className="resize-none"
                        data-testid={`third-party-security-${idx}`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
